import { useCallback, useEffect, useState } from 'react'
import Table from '../../components/Table'
import Pagination from '../../components/Pagination'
import ConfirmationModal from '../../components/modals/ConfirmationModal'
import AddVenueModal from '../../components/modals/AddVenueModal'
import VenueSuggestionDetailModal from '../../components/modals/VenueSuggestionDetailModal'
import FeedbackThreadModal from '../../components/FeedbackThreadModal'
import { VenueSuggestionDto } from '@ivy-monorepo/shared'
import { adminService } from '../../services/admin.service'

// User-submitted venues awaiting admin review — a separate resource from the
// venues list itself, so it gets its own fetch loop and table rather than
// reusing the "active"/"closed" status-filter tabs in Venues.tsx.
const VenueSuggestionsTable = () => {
  const [suggestions, setSuggestions] = useState<VenueSuggestionDto[]>([])
  const [page, setPage] = useState(1)
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    pageSize: 10,
    totalPages: 0,
    hasNext: false,
    hasPrev: false,
  })
  const [selectedSuggestion, setSelectedSuggestion] = useState<VenueSuggestionDto | null>(null)
  const [approvingSuggestion, setApprovingSuggestion] = useState<VenueSuggestionDto | null>(null)
  const [rejectingSuggestion, setRejectingSuggestion] = useState<VenueSuggestionDto | null>(null)
  const [openFeedbackCommentPublicId, setOpenFeedbackCommentPublicId] = useState<string | null>(null)
  // The suggestion the open feedback thread came from — its modal reopens on Back.
  const [feedbackReturnSuggestion, setFeedbackReturnSuggestion] = useState<VenueSuggestionDto | null>(null)

  const fetchSuggestions = useCallback(async () => {
    try {
      const response = await adminService.getVenueSuggestions(page, 10, 'Pending')
      setSuggestions(response.suggestions)
      setPagination(response.pagination)
    } catch (err) {
      console.log('Failed to fetch venue suggestions', err)
    }
  }, [page])

  useEffect(() => {
    fetchSuggestions()
  }, [fetchSuggestions])

  const handleReject = async () => {
    if (!rejectingSuggestion) return
    await adminService.rejectVenueSuggestion(rejectingSuggestion.publicId)
    setRejectingSuggestion(null)
    fetchSuggestions()
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
        <Pagination currentPage={pagination.page} totalPages={pagination.totalPages} onPageChange={setPage} />
      </div>

      <div className="venues-table-container">
        <div className="table-wrapper">
          <Table
            id="venue-suggestions"
            name="Venue suggestions"
            keyField="publicId"
            columns={[
              { label: 'Name', accessor: 'name', type: 'text' },
              { label: 'Comment', accessor: 'comment', type: 'text' },
              { label: 'Submitted by', accessor: 'submittedByName', type: 'text' },
              { label: 'Submitted', accessor: 'createdAt', type: 'datetime' },
            ]}
            sourceData={suggestions}
            isPropertyBarVisible={false}
            filterTerms=""
            disableGlobalSearch={true}
            disableClientSort={true}
            onRowClick={(row: VenueSuggestionDto) => setSelectedSuggestion(row)}
          />
        </div>
      </div>

      <VenueSuggestionDetailModal
        isOpen={selectedSuggestion !== null}
        onClose={() => setSelectedSuggestion(null)}
        suggestion={selectedSuggestion}
        onApprove={suggestion => {
          setSelectedSuggestion(null)
          setApprovingSuggestion(suggestion)
        }}
        onReject={suggestion => {
          setSelectedSuggestion(null)
          setRejectingSuggestion(suggestion)
        }}
        onOpenFeedback={suggestion => {
          setSelectedSuggestion(null)
          setFeedbackReturnSuggestion(suggestion)
          setOpenFeedbackCommentPublicId(suggestion.commentPublicId)
        }}
      />

      <AddVenueModal
        isOpen={approvingSuggestion !== null}
        onClose={() => setApprovingSuggestion(null)}
        onCreated={fetchSuggestions}
        initialValues={
          approvingSuggestion
            ? {
                name: approvingSuggestion.name,
                address: approvingSuggestion.address ?? '',
                latitude: approvingSuggestion.latitude != null ? String(approvingSuggestion.latitude) : '',
                longitude: approvingSuggestion.longitude != null ? String(approvingSuggestion.longitude) : '',
              }
            : undefined
        }
        onSubmit={
          approvingSuggestion
            ? payload => adminService.approveVenueSuggestion(approvingSuggestion.publicId, payload)
            : undefined
        }
      />

      <ConfirmationModal
        isOpen={rejectingSuggestion !== null}
        onClose={() => setRejectingSuggestion(null)}
        onConfirm={handleReject}
        title="Reject venue suggestion"
      >
        {rejectingSuggestion ? `Reject the suggestion for "${rejectingSuggestion.name}"? This cannot be undone.` : ''}
      </ConfirmationModal>

      <FeedbackThreadModal
        commentPublicId={openFeedbackCommentPublicId}
        isOpen={openFeedbackCommentPublicId !== null}
        onClose={() => {
          setOpenFeedbackCommentPublicId(null)
          setFeedbackReturnSuggestion(null)
        }}
        onBack={
          feedbackReturnSuggestion
            ? () => {
                setOpenFeedbackCommentPublicId(null)
                setSelectedSuggestion(feedbackReturnSuggestion)
                setFeedbackReturnSuggestion(null)
              }
            : undefined
        }
        backLabel="Back to suggestion"
      />
    </div>
  )
}

export default VenueSuggestionsTable
